import React from "react";
import { FranklinMadeMark } from "@/domains/documents/franklinMade/FranklinMadeMark";
import { useFranklinDisplayName } from "@/domains/documents/franklinMade/franklinMade";
import { observer } from "mobx-react-lite";
import { cn } from "@/lib/utils";
import {
  Eye,
  Download,
  Plus,
  MoreHorizontal,
  Pencil,
  FileText,
  BadgeCheck,
  MessageSquare,
} from "lucide-react";
import { FileTypeIcon } from "@/components/ui/documents";
import { VersionBadge } from "@/components/ui/documents/version-badge";
import { VerifiedBadge } from "@/components/ui/documents/verified-badge";
import { CommentCountBadge } from "../../comments/CommentCountBadge";
import type { DocumentCommentCounts } from "@/stores/documents/comments/types";
import { format } from "date-fns";
import { et } from "date-fns/locale";
import type {
  ContentMatchInfo,
  DocumentRowMapped,
  TimelineDateBasis,
} from "@/stores/documents/repository";
import { formatContentMatchTooltip } from "../../../utils/contentMatchTooltip";
import { TimelineDot, TimelineConnector, TimelineDocumentTags } from "../../atoms/Timeline";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { FolderPathSegment } from "../../../utils/folderPathResolver";
import { BreadcrumbTooltipContent } from "../../atoms/BreadcrumbTooltipContent";
import { DocumentNameInlineEdit } from "../../atoms/DocumentNameInlineEdit";
import { DocumentTagEditorButton } from "../../atoms/DocumentTagEditorButton";
import { LinkedIssuesChip } from "@/domains/pm/components/issues/LinkedIssuesChip";
import { SignedFileIcon } from "../../atoms/SignedContainerIndicators";
import { EnclosedDocumentsMenu } from "../EnclosedDocumentsMenu";
import { useSignaturePanel } from "../../../signing/signaturePanelContext";
import { canInspectSignatures } from "../../../signing/signatureInspection";
import { useSignedContainerInfo } from "../../../signing/useSignedContainerInfo";
import { DocumentRowContextMenu } from "../../organisms/DocumentRowContextMenu";
import type { DocumentMenuConfig } from "../../organisms/DocumentRowContextMenu";
import { isSignedContainerFile } from "../../../constants/fileTypes";
import { documentQuickViewKind } from "../../../hooks/useDrawingViewer";
import { TIMELINE_DOCUMENT_CARD_FILL_CLASS } from "./timelineCard";
import { getDocTimestamp } from "@/stores/documents/repository/documentDates";
import { fileDateHint, resolveFileDate } from "../../../utils/documentDates";
import { DateHint } from "../../atoms/DateHint";

/**
 * Modifier keys held during a timeline selection click.
 * - `multiSelect` (Cmd/Ctrl, or a checkbox toggle): accumulate — toggle this
 *   row without clearing the rest.
 * - `rangeSelect` (Shift): select the range from the anchor row to this one.
 * Plain click (neither) toggles this row, keeping the existing selection.
 */
export interface TimelineSelectModifiers {
  multiSelect: boolean;
  rangeSelect: boolean;
}

interface ServerTimelineNodeProps {
  /** The document to display */
  document: DocumentRowMapped;
  /** Which side of the timeline stem */
  side: "left" | "right";
  /** Pixel position from top of container — only read by the absolutely
   *  positioned `default` variant. */
  pixelPosition?: number;
  /** Whether this node is selected */
  isSelected: boolean;
  /** Callback when node is clicked (view) */
  onView: (document: DocumentRowMapped) => void;
  /** Callback when selection changes. `modifiers` reports the keys held so the
   *  parent can accumulate (Cmd/Ctrl or checkbox), range-select (Shift), or
   *  plain-toggle — matching the documents list table's behaviour. */
  onSelect: (
    document: DocumentRowMapped,
    selected: boolean,
    modifiers: TimelineSelectModifiers
  ) => void;
  /** Callback when download is clicked */
  onDownload: (document: DocumentRowMapped) => void;
  /** Callback when Teach Franklin is triggered (e.g. clicking overflow tags badge) */
  onTeachFranklin?: (document: DocumentRowMapped) => void;
  /** Callback when a tag's (x) remove button is clicked */
  onTagRemove?: (
    document: DocumentRowMapped,
    tagType: "category" | "discipline" | "stage",
    tagId: number,
    tagName: string
  ) => void;
  /** Callback when the version badge is clicked — opens version history. */
  onShowVersionHistory?: (document: DocumentRowMapped) => void;
  /** Open this DWG in the Drawings tool. Only supplied for a parent .dwg the
   *  user may open, so its presence is the whole gate — for the overflow item
   *  AND for the eye icon, which routes a DWG here instead of to `onView`. */
  onViewDrawing?: () => void;
  /** Structured breadcrumb segments for interactive tooltip navigation */
  breadcrumb?: FolderPathSegment[] | null;
  /** Building ID for breadcrumb navigation links */
  buildingId?: string;
  /** Variant — `default` positions the card absolutely at `pixelPosition` with
   *  connector/dot, `flow` keeps the connector/dot but lets the card sit in
   *  normal flow (the Days view stacks a day's cards in a column), `compact`
   *  drops connector and dot entirely (cluster popover). */
  variant?: "default" | "flow" | "compact";
  /** Additional CSS classes */
  className?: string;
  /** Callback when Rename is selected from the kebab menu. When undefined, the
   *  kebab is hidden — the parent uses this to gate visibility by permission. */
  onRename?: (document: DocumentRowMapped) => void;
  /** Toggle this row's Verified mark from the kebab menu. Undefined hides the
   *  item — the parent uses this to gate visibility by permission. */
  onToggleVerified?: (document: DocumentRowMapped) => void;
  /** Open the comments panel on this card's document. Undefined hides the item. */
  onComments?: (document: DocumentRowMapped) => void;
  /** Open / completed thread counts for the badge beside the Verified mark. */
  commentCounts?: DocumentCommentCounts;
  /** Which date places this card — and so which one it leads with. */
  dateBasis?: TimelineDateBasis;
  /** Right-click menu config for this card. Undefined leaves the browser's
   *  native menu in place. Built by the timeline so every node variant —
   *  compact, flow and the default stem card — gets the same one. */
  contextMenu?: DocumentMenuConfig;
  /** When true, render an inline-edit input over the filename. */
  isRenaming?: boolean;
  /** Editable basename (without extension) — only used when isRenaming. */
  renameBasename?: string;
  /** File extension (with leading dot) — preserved during rename. */
  renameExtension?: string;
  /** Called as the user types during rename. */
  onRenameChange?: (value: string) => void;
  /** Called when rename is confirmed (Enter or blur). */
  onRenameConfirm?: () => void;
  /** Called when rename is cancelled (Escape). */
  onRenameCancel?: () => void;
  /** True when this document was found via content (not filename) match — shows badge. */
  isContentMatch?: boolean;
  /** First-match page/sheet + total match count for the Content chip tooltip
   *  (WHA-3169). Falls back to the legacy "Found in document text" string
   *  when omitted. */
  contentMatchInfo?: ContentMatchInfo | null;
}

/**
 * Format file size for display
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * ServerTimelineNode - Single document node for server-side timeline
 *
 * Displays a single document on the timeline with checkbox, version badge,
 * view/download icons, "Added by" attribution, and tag badges.
 * Works with DocumentRowMapped from server-side queries.
 */
export const ServerTimelineNode = observer(function ServerTimelineNode({
  document,
  side,
  pixelPosition,
  isSelected,
  onView,
  onSelect,
  onDownload,
  onTeachFranklin,
  onTagRemove,
  onShowVersionHistory,
  breadcrumb,
  buildingId,
  variant = "default",
  className,
  onRename,
  onViewDrawing,
  onToggleVerified,
  onComments,
  commentCounts,
  dateBasis = "file",
  contextMenu,
  isRenaming = false,
  renameBasename = "",
  renameExtension = "",
  onRenameChange,
  onRenameConfirm,
  onRenameCancel,
  isContentMatch = false,
  contentMatchInfo,
}: ServerTimelineNodeProps) {
  const { t, language } = useLanguage();
  // Franklin's appendices PDF reads in the viewer's language; others as stored.
  const displayFilename = useFranklinDisplayName(
    buildingId,
    document.id,
    document.filename,
    language
  );
  // Signed container context — the same seal/chip/panel the list rows use.
  // Only the row itself can be a container here: the timeline RPCs keep
  // documents enclosed in a signed container off the timeline, so
  // `parent_document_id` (DWG child PDFs, plain `.zip` children) is deliberately
  // not passed — chasing it would fire one lookup per distinct parent that can
  // only ever resolve to null.
  // DEPLOY DEPENDENCY: that RPC exclusion is franklin-data-pipeline migration
  // 20260728…_timeline_signed_containers_as_rows (pipeline PR 212). This
  // frontend must not reach production before it; without it enclosed children
  // come back from the RPC and render as plain rows, seal-less.
  const signaturePanel = useSignaturePanel();
  const canViewSignatures = !!signaturePanel && canInspectSignatures(document.filename);
  const showActionsMenu =
    onRename || onViewDrawing || onToggleVerified || onComments || canViewSignatures;
  const signed = useSignedContainerInfo({ id: document.id, filename: document.filename });
  const isSignedContainerRow = isSignedContainerFile(document.filename);
  const openContainerPanel =
    isSignedContainerRow && signaturePanel
      ? () => signaturePanel.open({ containerId: String(document.id) })
      : undefined;
  // The seal on the file icon is the ONLY signature status the timeline shows —
  // no written status text anywhere in the document views.
  // A signed container is a sealed envelope, not a previewable file: its title
  // and its eye icon open the signature panel — the same click the Folders and
  // List rows give it — instead of trying to render the .asice itself.
  // The same date the row is PLACED by — see getDocTimestamp. Deriving it here
  // instead is how a card came to print "26 Aug" inside a 21 July bucket.
  // The same date the row is PLACED by — see getDocTimestamp. Deriving it here
  // instead is how a card came to print "26 Aug" inside a 21 July bucket.
  const timestamp = getDocTimestamp(document, dateBasis);
  // Localize the date/time to Estonian when the app language is `et`:
  // day-first date with Estonian month names, and 24-hour time (Estonia does
  // not use AM/PM). English keeps its original "MMM d, yyyy" / "h:mm a" form.
  const dateLocale = language === "et" ? et : undefined;
  const formatCardDateTime = (date: Date) =>
    `${format(date, language === "et" ? "d. MMM yyyy" : "MMM d, yyyy", {
      locale: dateLocale,
    })} ${format(date, language === "et" ? "HH:mm" : "h:mm a", { locale: dateLocale })}`;
  // The card leads with the date it is placed by and names the other one on
  // the line below, each with the tooltip saying which date it is.
  const fileDate = resolveFileDate(document);
  const fileDateTooltip = fileDate ? fileDateHint(fileDate, t.documents.table) : null;
  const uploadedAt = new Date(document.created_at);
  const uploadedStr = Number.isNaN(uploadedAt.getTime()) ? null : formatCardDateTime(uploadedAt);
  const placedByUpload = dateBasis === "upload";
  const primaryDateHint = placedByUpload ? t.documents.table.uploadedTooltip : fileDateTooltip;
  const secondaryDate = placedByUpload
    ? fileDate && fileDateTooltip
      ? {
          label: t.documents.table.dateModified,
          text: formatCardDateTime(getDocTimestamp(document, "file")),
          hint: fileDateTooltip,
        }
      : null
    : uploadedStr
      ? { label: t.timeline.uploaded, text: uploadedStr, hint: t.documents.table.uploadedTooltip }
      : null;

  // Card click toggles selection and keeps the existing selection (multi-select
  // by default, like the list table). Cmd/Ctrl also accumulates; Shift selects
  // a range from the anchor.
  const handleCardClick = (e: React.MouseEvent) => {
    // Radix menu content is portalled. Its React click still bubbles through
    // this card even though its DOM node is elsewhere, so ignore it rather than
    // toggling the row after a menu action.
    if (e.target instanceof Node && !e.currentTarget.contains(e.target)) return;
    onSelect(document, !isSelected, {
      multiSelect: e.metaKey || e.ctrlKey,
      rangeSelect: e.shiftKey,
    });
  };

  // The browser starts the shift-click text highlight on mousedown — click-time
  // preventDefault is too late — so suppress it here while range-selecting.
  const handleCardMouseDown = (e: React.MouseEvent) => {
    if (e.shiftKey) e.preventDefault();
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Modifier clicks on the checkbox range-select/accumulate exactly like the
  // list table (WHA-2995). Capture phase, so the event never reaches Radix's
  // own toggle — which would fire onCheckedChange and undo the range.
  const handleCheckboxClickCapture = (e: React.MouseEvent) => {
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      e.preventDefault();
      e.stopPropagation();
      onSelect(document, !isSelected, {
        multiSelect: e.metaKey || e.ctrlKey,
        rangeSelect: e.shiftKey,
      });
    }
  };

  const handleCheckedChange = (checked: boolean) => {
    // Checkbox toggle always accumulates (doesn't clear others).
    onSelect(document, checked, { multiSelect: true, rangeSelect: false });
  };

  // What the eye icon actually opens — undefined means this row has no quick
  // view, so no button is rendered. Same routing as the List view's row
  // (`documentQuickViewKind`): a `.dwg` goes to the Drawings tool, never to the
  // document viewer, whose DWG branch renders a converted child PDF this branch
  // removed. Without the drawing-tool grant a DWG has no quick view at all —
  // and a signed container with no signature panel mounted has none either.
  const quickViewKind = documentQuickViewKind(document, !!onViewDrawing, false);
  const viewAction = isSignedContainerRow
    ? openContainerPanel
    : quickViewKind === "drawing"
      ? onViewDrawing
      : quickViewKind === "preview"
        ? () => onView(document)
        : undefined;

  const handleViewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    viewAction?.();
  };

  const handleDownloadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDownload(document);
  };

  // Keyboard: Enter/Space toggles selection (accumulates, like a plain click).
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(document, !isSelected, { multiSelect: false, rangeSelect: false });
    }
  };

  const isCompact = variant === "compact";

  // The container's title opens the panel; every other document keeps the plain
  // label the timeline has always shown.
  const filenameTrigger = openContainerPanel ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        openContainerPanel();
      }}
      className="min-w-0 flex-1 truncate text-left text-sm font-semibold text-[#101828] hover:text-primary hover:underline"
    >
      {displayFilename}
    </button>
  ) : (
    <p className="min-w-0 flex-1 cursor-default truncate text-sm font-semibold text-[#101828]">
      {displayFilename}
    </p>
  );

  const card = (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Select document ${document.filename}`}
      onClick={handleCardClick}
      onMouseDown={handleCardMouseDown}
      onKeyDown={handleKeyDown}
      className={cn(
        // Light-grey fill + a defined border so the card still reads as its
        // own component on the white canvas.
        "group/card flex cursor-pointer items-start gap-3 rounded-[10px] border",
        TIMELINE_DOCUMENT_CARD_FILL_CLASS,
        "px-3 py-2 shadow-sm transition-all duration-200",
        "hover:shadow-lg",
        "w-full",
        isSelected && "border-primary shadow-md ring-2 ring-primary/20",
        !isSelected && "border-[#e5e7eb] hover:border-muted-foreground/30"
      )}
    >
      {/* Checkbox */}
      <div
        className="flex flex-shrink-0 pt-[3px]"
        onClick={handleCheckboxClick}
        onClickCapture={handleCheckboxClickCapture}
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={handleCheckedChange}
          aria-label={`Select ${document.filename}`}
          className="h-4 w-4 rounded border-2 border-[#99a1af] data-[state=checked]:border-primary"
        />
      </div>

      {/* Content area - icon is inside row 1, not separate */}
      <div className="min-w-0 flex-1">
        {/* Row 1: Icon + Filename + version badge + view icon */}
        <div className="flex items-center gap-3">
          {/* Small file icon - no big background */}
          <div className="flex-shrink-0">
            {isSignedContainerRow ? (
              <SignedFileIcon
                filename={document.filename}
                status={signed?.info.status ?? "unverified"}
                iconClassName="h-5 w-5"
              />
            ) : (
              <FileTypeIcon filename={document.filename} className="h-5 w-5" />
            )}
          </div>
          {isRenaming ? (
            <DocumentNameInlineEdit
              basename={renameBasename}
              extension={renameExtension}
              onChange={(value) => onRenameChange?.(value)}
              onConfirm={() => onRenameConfirm?.()}
              onCancel={() => onRenameCancel?.()}
              ariaLabel={`Rename ${document.filename}`}
              className="flex-1"
            />
          ) : breadcrumb && buildingId ? (
            <HoverCard openDelay={300} closeDelay={200}>
              <HoverCardTrigger asChild>{filenameTrigger}</HoverCardTrigger>
              <HoverCardContent
                side="bottom"
                align="start"
                className="w-fit max-w-lg break-words p-2 text-sm"
              >
                <BreadcrumbTooltipContent segments={breadcrumb} buildingId={buildingId} />
              </HoverCardContent>
            </HoverCard>
          ) : (
            filenameTrigger
          )}
          <FranklinMadeMark buildingId={buildingId} documentId={document.id} />
          {isSignedContainerRow && (
            <EnclosedDocumentsMenu
              containerId={String(document.id)}
              containerFilename={document.filename}
            />
          )}
          {isContentMatch && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
                    <FileText className="h-3 w-3" />
                    {t.documents.table.contentMatch}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {formatContentMatchTooltip(contentMatchInfo, t.documents.table)}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {/* Verified mark — same placement as the List view's, so the two
              tabs read identically. */}
          <VerifiedBadge
            verifiedAt={document.verified_at}
            verifiedByName={document.verified_by_name}
          />
          <CommentCountBadge
            counts={commentCounts}
            onClick={onComments ? () => onComments(document) : undefined}
          />
          {/* WHA-3270: chip for PM issues this doc is attached to. It leads the
              action row — a document's link to an issue outranks its version
              and the open/see-more controls that follow. */}
          {document.linked_issues && document.linked_issues.length > 0 && (
            <LinkedIssuesChip issues={document.linked_issues} />
          )}
          {document.version && (
            <VersionBadge
              version={document.version}
              hasHistory={
                (document.version ?? 1) > 1 && !!document.version_group && !!onShowVersionHistory
              }
              onClick={onShowVersionHistory ? () => onShowVersionHistory(document) : undefined}
            />
          )}
          {viewAction && (
            <button
              type="button"
              onClick={handleViewClick}
              className="flex-shrink-0 rounded p-1.5 text-[#6a7282] transition-colors hover:bg-muted hover:text-foreground"
              aria-label={`View ${document.filename}`}
            >
              <Eye className="h-4 w-4" />
            </button>
          )}
          {showActionsMenu && !isRenaming && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 flex-shrink-0 p-0 text-[#6a7282] hover:bg-muted hover:text-foreground"
                  onClick={(e) => e.stopPropagation()}
                  aria-label={t.documents.actions2.openMenu}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                {canViewSignatures && (
                  <DropdownMenuItem
                    onClick={() => signaturePanel?.open({ containerId: String(document.id) })}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    {t.documents.signedContainer.panel.viewSignatures}
                  </DropdownMenuItem>
                )}
                {onRename && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onRename(document);
                    }}
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    {t.documents.actions2.rename}
                  </DropdownMenuItem>
                )}
                {onViewDrawing && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewDrawing();
                    }}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    {t.documents.bulkActions.viewDrawing}
                  </DropdownMenuItem>
                )}
                {onComments && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onComments(document);
                    }}
                  >
                    <MessageSquare className="mr-2 h-4 w-4" />
                    {t.documents.bulkActions.comments}
                  </DropdownMenuItem>
                )}
                {onToggleVerified && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleVerified(document);
                    }}
                  >
                    <BadgeCheck className="mr-2 h-4 w-4" />
                    {document.verified_at
                      ? t.documents.bulkActions.unverify
                      : t.documents.bulkActions.verify}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Row 2: Size and date/time - aligned with icon left edge */}
        <div className="mt-0.5 flex items-center gap-2 text-xs text-[#6a7282]">
          <span>{formatFileSize(document.size || 0)}</span>
          <span>•</span>
          {primaryDateHint ? (
            <DateHint hint={primaryDateHint}>{formatCardDateTime(timestamp)}</DateHint>
          ) : (
            <span>{formatCardDateTime(timestamp)}</span>
          )}
        </div>

        {/* Row 3: the other date + Added by attribution */}
        {(secondaryDate || document.created_by_name) && (
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-[#6a7282]">
            {secondaryDate && (
              <DateHint hint={secondaryDate.hint}>
                {secondaryDate.label} {secondaryDate.text}
              </DateHint>
            )}
            {secondaryDate && document.created_by_name && <span>•</span>}
            {document.created_by_name && (
              <span>{t.timeline.addedBy.replace("{name}", document.created_by_name)}</span>
            )}
          </p>
        )}

        {/* Row 4: Tags + Download icon */}
        <div className="mt-1 flex items-end gap-2">
          {/* Tags wrapper - always exists to keep download at right */}
          <div className="min-w-0 flex-1">
            <TimelineDocumentTags
              categories={document.categories}
              disciplines={document.design_disciplines}
              stages={document.design_stages}
              maxVisible={4}
              onOverflowClick={onTeachFranklin ? () => onTeachFranklin(document) : undefined}
              onTagRemove={
                onTagRemove
                  ? (tagType, tagId, tagName) => onTagRemove(document, tagType, tagId, tagName)
                  : undefined
              }
              suffix={
                onTeachFranklin ? (
                  <DocumentTagEditorButton
                    empty={
                      !document.categories?.length &&
                      !document.design_disciplines?.length &&
                      !document.design_stages?.length
                    }
                    onClick={() => onTeachFranklin(document)}
                  />
                ) : undefined
              }
            />
          </div>
          <button
            type="button"
            onClick={handleDownloadClick}
            className="flex-shrink-0 self-end text-[#6a7282] transition-colors hover:text-foreground"
            aria-label={`Download ${document.filename}`}
          >
            <Download className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  // One wrap for every variant below. Disabled mid-rename so the inline input
  // keeps the browser's native text menu (copy/paste), matching the list rows.
  const cardContent = contextMenu ? (
    <DocumentRowContextMenu variant="document" disabled={isRenaming} {...contextMenu}>
      {card}
    </DocumentRowContextMenu>
  ) : (
    card
  );

  // Compact variant: no connector/dot, just the card
  if (isCompact) {
    return cardContent;
  }

  // Give every stem-connected card one content-independent flex column. The
  // card itself is `w-full`; without this min-w-0/flex-1 boundary it is the
  // flex item beside the fixed connector + dot, so the browser shrinks it from
  // its min-content width. Long filenames and extra controls (notably signed
  // containers) then produce wider cards whose outer edge no longer aligns
  // with their neighbours at narrower viewport widths.
  const cardColumn = (
    <div className={cn("min-w-0 flex-1", side === "left" ? "mr-3" : "ml-3")}>{cardContent}</div>
  );

  // Flow variant: connector and dot, but the card sits in normal flow so a
  // stack of them can be spaced by its container (the Days view groups a day's
  // cards tightly together).
  if (variant === "flow") {
    return (
      <div
        className={cn(
          "group flex w-full items-center",
          side === "left" ? "flex-row-reverse" : "flex-row",
          className
        )}
      >
        <TimelineConnector direction={side} />
        <TimelineDot variant={isSelected ? "selected" : "default"} size="md" />
        {cardColumn}
      </div>
    );
  }

  // Default variant: with connector and dot
  return (
    <div
      className={cn(
        "group absolute flex items-center transition-all duration-300 ease-out",
        side === "left"
          ? "left-6 right-1/2 flex-row-reverse pr-2"
          : "left-1/2 right-6 flex-row pl-2",
        className
      )}
      style={{ top: pixelPosition, transform: "translateY(-50%)" }}
    >
      {/* Connector line */}
      <TimelineConnector direction={side} />

      {/* Dot on the stem */}
      <TimelineDot variant={isSelected ? "selected" : "default"} size="md" />

      {/* Document card */}
      {cardColumn}
    </div>
  );
});
